import React, { useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Tab,
    TextField,
    Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DayCalendarSkeleton, PickersDay } from "@mui/x-date-pickers";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import './lodging.css';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function LodgingDialogEditDates({ isOpen, onClose, eventLodging, setEventLodging }) {
    const { PA_GlobalObject } = usePA_GlobalStore();

    const [eventHasDates] = useState(() => PA_GlobalObject?.Planning?.time_table?.dates?.length > 0);
    const [eventHasDays] = useState(() => PA_GlobalObject?.Planning?.time_table?.days?.length > 0);

    const [tabValue, setTabValue] = useState(eventHasDates || !eventHasDays ? "dates" : "days");

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // ─── DATE PICKING RANGE LOGIC ───────────────────────────────────────
    const [beginDate, setBeginDate] = useState(() =>
        eventLodging.time_table?.dates?.[0] ? dayjs(eventLodging.time_table.dates[0].date) : null
    );

    const [endDate, setEndDate] = useState(() =>
        eventLodging.time_table?.dates?.length > 1
            ? dayjs(eventLodging.time_table.dates[eventLodging.time_table.dates.length - 1].date)
            : null
    );

    const [selectedDates, setSelectedDates] = useState(() => {
        if (beginDate && endDate) {
            const range = [];
            let current = beginDate.startOf('day');
            const last = endDate.startOf('day');
            while (current.isSameOrBefore(last, 'day')) {
                range.push(current);
                current = current.add(1, 'day');
            }
            return range;
        } else if (beginDate) {
            return [beginDate];
        }
        return [];
    });

    const handleDateSelect = (day) => {
        if (!beginDate || (beginDate && endDate)) {
            setBeginDate(day);
            setEndDate(null);
            setSelectedDates([day]);
        } else if (beginDate && !endDate) {
            if (day.isBefore(beginDate, 'day')) {
                setBeginDate(day);
                setSelectedDates([day]);
            } else {
                setEndDate(day);
                const range = [];
                let current = beginDate.startOf('day');
                const last = day.startOf('day');
                while (current.isSameOrBefore(last, 'day')) {
                    range.push(current);
                    current = current.add(1, 'day');
                }
                setSelectedDates(range);
            }
        }
    };

    // ─── DYNAMIC DURATION CALCULATION ──────────────────────────────────
    const totalNights = useMemo(() => {
        if (beginDate && endDate) {
            return endDate.diff(beginDate, 'day');
        }
        return 0;
    }, [beginDate, endDate]);

    const totalDays = useMemo(() => {
        return selectedDates.length;
    }, [selectedDates]);

    // ─── HIGHLIGHTING & SERVER DAY MARKERS ─────────────────────────────
    const getMarkersForDate = (date, _planning) => {
        if (!_planning) return [];
        const markers = [];

        const mealDates = (_planning.meals || []).flatMap((entry) =>
            (entry.time_table?.dates || []).map((item) => dayjs(item.date, "YYYY-MM-DD"))
        );
        if (mealDates.some((mealDate) => mealDate.isSame(date, "day"))) {
            markers.push("meal");
        }

        const activityDates = (_planning.activities || []).flatMap((entry) =>
            (entry.time_table?.dates || []).map((item) => dayjs(item.date, "YYYY-MM-DD"))
        );
        if (activityDates.some((activityDate) => activityDate.isSame(date, "day"))) {
            markers.push("activity");
        }

        return markers;
    };

    const isHighlighted = (date) => {
        return selectedDates.some(selectedDate => date.isSame(selectedDate, 'day'));
    };

    function ServerDay(props) {
        const { day, outsideCurrentMonth, planningData, ...other } = props;
        const isSelected = !outsideCurrentMonth && isHighlighted(day);
        const markers = getMarkersForDate(day, planningData);

        return (
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                onClick={() => handleDateSelect(day)}
                sx={{
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    ...(isSelected
                        ? {
                            backgroundColor: "#1976d2 !important",
                            color: "white !important",
                            borderRadius: "50%",
                            boxShadow: '0 4px 10px rgba(25, 118, 210, 0.4)',
                        }
                        : {
                            backgroundColor: "transparent",
                            color: outsideCurrentMonth ? "#ccc" : "text.primary",
                            border: dayjs().isSame(dayjs(day), 'day') ? '1px solid #1976d2' : 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                        })
                }}
            >
                <span>{day.format("D")}</span>

                {markers.length > 0 && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            display: "flex",
                            gap: "3px",
                        }}
                    >
                        {markers.map((marker, index) => (
                            <span
                                key={index}
                                style={{
                                    width: "5px",
                                    height: "5px",
                                    borderRadius: "50%",
                                    backgroundColor: marker === "meal" ? "#FF6347" : "#FFD700",
                                }}
                            />
                        ))}
                    </Box>
                )}
            </PickersDay>
        );
    }

    // ─── CHECK IN / CHECK OUT TIME CONFIGURATION ───────────────────────────
    const rawBeginTime = eventLodging.time_table?.dates?.[0]?.checkin_time || null;
    const rawEndTime = eventLodging.time_table?.dates?.[eventLodging.time_table?.dates?.length - 1]?.checkout_time || null;

    const [selectedLodgingCheckinTime, setSelectedLodgingCheckinTime] = useState(() => rawBeginTime ? dayjs(rawBeginTime, 'h:mm A') : null);
    const [selectedLodgingCheckoutTime, setSelectedLodgingCheckoutTime] = useState(() => rawEndTime ? dayjs(rawEndTime, 'h:mm A') : null);

    const initialDate = useMemo(() => {
        const timeTableDate = eventLodging?.time_table?.dates?.[0]?.date;
        if (timeTableDate && dayjs(timeTableDate).isValid()) return dayjs(timeTableDate, "YYYY-MM-DD");
        const planningDate = PA_GlobalObject?.Planning?.time_table?.dates?.[0]?.date;
        if (planningDate && dayjs(planningDate).isValid()) return dayjs(planningDate, "YYYY-MM-DD");
        return dayjs();
    }, [eventLodging, PA_GlobalObject]);

    // ─── PERSIST SAVED STATE OPTIONS ────────────────────────────────────────
    const saveEventLodgingSettings = () => {
        if (!selectedDates || selectedDates.length === 0) return;

        const dates = selectedDates.map((day, idx) => ({
            date: day.format('YYYY-MM-DD'),
            dayOfWeek: day.format('dddd'),
            checkin_time: idx === 0 && selectedLodgingCheckinTime ? selectedLodgingCheckinTime.format('h:mm A') : undefined,
            checkout_time: idx === selectedDates.length - 1 && selectedLodgingCheckoutTime ? selectedLodgingCheckoutTime.format('h:mm A') : undefined,
            notes: '',
        }));

        const updatedLodging = {
            ...eventLodging,
            time_table: { dates, days: [], id: '' }
        };
        setEventLodging(updatedLodging);

        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 360,
                    maxWidth: 460,
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)'
                },
            }}
        >
            <DialogTitle sx={{ color: '#151269', textAlign: 'center', backgroundColor: '#f0f7ff', fontWeight: '600', p: 2 }}>
                Lodging Scheduler
            </DialogTitle>
            <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: '#151269' }}>
                <i className="icon-cross" style={{ fontSize: '16px' }} />
            </IconButton>

            <DialogContent sx={{ p: 2, '&::-webkit-scrollbar': { display: 'none' } }}>
                <TabContext value={tabValue}>
                    <TabList sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }} centered variant="fullWidth" onChange={handleTabChange}>
                        <Tab label="Specific Dates" value="dates" sx={{ fontWeight: '600' }} />
                        <Tab label="Days of Event" value="days" sx={{ fontWeight: '600' }} />
                    </TabList>

                    <TabPanel value="dates" sx={{ p: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
                            <DateCalendar
                                sx={{ width: '100%', height: 'auto' }}
                                value={selectedDates.length ? selectedDates[0] : null}
                                onChange={handleDateSelect}
                                defaultValue={initialDate}
                                referenceDate={initialDate}
                                renderLoading={() => <DayCalendarSkeleton />}
                                showDaysOutsideCurrentMonth
                                slots={{ day: ServerDay }}
                                slotProps={{
                                    day: { planningData: PA_GlobalObject?.Planning },
                                }}
                            />

                            {/* Visual Legends */}
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, mb: 1, width: '100%', justifyContent: 'center' }}>
                                {PA_GlobalObject?.Planning?.meals?.some(m => m.time_table?.dates?.length > 0) && (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FF6347' }} />
                                        <Typography variant="caption" color="text.secondary">Meals</Typography>
                                    </Box>
                                )}
                                {PA_GlobalObject?.Planning?.activities?.some(a => a.time_table?.dates?.length > 0) && (
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FFD700' }} />
                                        <Typography variant="caption" color="text.secondary">Activities</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </TabPanel>

                    <TabPanel value="days" sx={{ p: 1 }}>
                        {eventHasDays ? (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                Dynamic roadway operational timeline items map here.
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                No dynamic days found in current roadmap.
                            </Typography>
                        )}
                    </TabPanel>
                </TabContext>

                <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 2 }}>
                    <FormControl fullWidth>
                        <TimePicker
                            label="Check-in Time"
                            value={selectedLodgingCheckinTime}
                            onChange={(newTime) => setSelectedLodgingCheckinTime(newTime)}
                            ampmInClock
                            minutesStep={5}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <TimePicker
                            label="Check-out Time"
                            value={selectedLodgingCheckoutTime}
                            onChange={(newTime) => setSelectedLodgingCheckoutTime(newTime)}
                            ampmInClock
                            minutesStep={5}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </FormControl>
                </Box>

                {/* ─── ENHANCED 3-COLUMN SUMMARY AREA WITH DURATION ─────────────── */}
                <Box
                    sx={{
                        background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
                        border: '1px solid #cbd5e1',
                        borderRadius: '14px',
                        p: 1.5,
                        mt: 2,
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)'
                    }}
                >
                    {/* Check In Column */}
                    <Box sx={{ flex: 1.1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#64748b', fontWeight: 800, letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                            Check-In
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {beginDate ? beginDate.format('MMM DD, YYYY') : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                            {selectedLodgingCheckinTime ? selectedLodgingCheckinTime.format('h:mm A') : '—'}
                        </Typography>
                    </Box>

                    <Box sx={{ height: '36px', width: '1px', backgroundColor: '#cbd5e1' }} />

                    {/* Calculated Nights & Days Column */}
                    <Box sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#1976d2', fontWeight: 800, letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                            Duration
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            {totalNights} {totalNights === 1 ? 'Night' : 'Nights'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, display: 'block', mt: 0.2 }}>
                            {totalDays} {totalDays === 1 ? 'Day' : 'Days'}
                        </Typography>
                    </Box>

                    <Box sx={{ height: '36px', width: '1px', backgroundColor: '#cbd5e1' }} />

                    {/* Check Out Column */}
                    <Box sx={{ flex: 1.1, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#64748b', fontWeight: 800, letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                            Check-Out
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {endDate ? endDate.format('MMM DD, YYYY') : (beginDate ? beginDate.format('MMM DD, YYYY') : '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 500 }}>
                            {selectedLodgingCheckoutTime ? selectedLodgingCheckoutTime.format('h:mm A') : '—'}
                        </Typography>
                    </Box>
                </Box>
                {/* ───────────────────────────────────────────────────────────── */}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, backgroundColor: '#fcfcfc', borderTop: '1px solid #f0f0f0' }}>
                <Button onClick={onClose} variant="text" sx={{ color: 'text.secondary', fontWeight: '600' }}>Cancel</Button>
                <Button onClick={saveEventLodgingSettings} variant="contained" disableElevation sx={{ borderRadius: '8px', px: 3, fontWeight: '600', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
                    Save Schedule
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default LodgingDialogEditDates;